import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { RefreshCw, Mail, Paperclip, ExternalLink } from "lucide-react";
import GmailStatusCard from "@/components/GmailStatusCard";

const TRIAGE = [
  { v: "new", label: "New" },
  { v: "reviewed", label: "Reviewed" },
  { v: "action_needed", label: "Action needed" },
  { v: "waiting", label: "Waiting" },
  { v: "archived", label: "Archived" },
];

export default function InboxPage() {
  const [conn, setConn] = useState(null);
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [diag, setDiag] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const load = async () => {
    const [c, t] = await Promise.all([
      api.get("/inbox/connection"),
      api.get(`/inbox/threads?limit=100${filter ? `&triage=${filter}` : ""}`),
    ]);
    setConn(c.data); setThreads(t.data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  // Google → /inbox/oauth/callback?code=…&state=… landing. Exchange the
  // code for tokens exactly once, then strip the URL and refresh state.
  useEffect(() => {
    if (!location.pathname.endsWith("/inbox/oauth/callback")) return;
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const state = params.get("state");
    const err = params.get("error");
    if (err) {
      toast.error(`Google returned error: ${err}`);
      navigate("/inbox", { replace: true });
      return;
    }
    if (!code || !state) return;
    setConnecting(true);
    (async () => {
      try {
        await api.post("/inbox/oauth/callback", { code, state });
        toast.success("Gmail connected. Running initial sync…");
        navigate("/inbox", { replace: true });
        await api.post("/inbox/sync", {});
        await load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to complete Gmail connection");
        navigate("/inbox", { replace: true });
      } finally { setConnecting(false); }
    })();
    // eslint-disable-next-line
  }, [location.pathname]);

  const connect = async () => {
    const redirect = `${window.location.origin}/inbox/oauth/callback`;
    const r = await api.post("/inbox/oauth/start", { provider: "google", redirect_uri: redirect });
    window.location.href = r.data.authorize_url;
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect this inbox? Threads are kept.")) return;
    await api.post("/inbox/disconnect", {});
    await load();
  };
  const sync = async () => {
    setBusy(true);
    try {
      const r = await api.post("/inbox/sync", {});
      const inserted = r.data.inserted || 0;
      const dup = r.data.duplicate || 0;
      const errors = r.data.errors || 0;
      const retried = r.data.retried || 0;
      // Always surface retry activity so live drain state is never invisible.
      const retryLine = retried > 0
        ? `Retry queue: ${retried} attempted, ${retried - errors} recovered, ${errors} still failing`
        : "Retry queue empty";
      if (errors > 0 && retried === 0) {
        // Fresh failures with no prior queue drain.
        toast.error(`Sync partial: ${inserted} new, ${errors} queued for retry. Click Sync again to retry them.`, { duration: 6000 });
      } else if (retried > 0) {
        // Retry activity happened — show it whether success or partial.
        toast.success(`Sync: ${inserted} new, ${dup} known · ${retryLine}`, { duration: 8000 });
      } else {
        toast.success(`Sync: ${inserted} new, ${dup} known`);
      }
      await load();
    } catch (e) { toast.error("Sync failed"); }
    finally { setBusy(false); }
  };

  const recoverySync = async () => {
    setBusy(true);
    try {
      const r = await api.post("/inbox/recovery_sync", {});
      const inserted = r.data.inserted || 0;
      const dup = r.data.duplicate || 0;
      const errors = r.data.errors || 0;
      const retried = r.data.retried || 0;
      toast.success(`Recovery sync: ${inserted} recovered, ${dup} known, ${errors} still failing. Retry queue: ${retried} attempted from previous queue.`, { duration: 10000 });
      await load();
    } catch (e) { toast.error("Recovery sync failed"); }
    finally { setBusy(false); }
  };
  const openThread = async (t) => {
    const r = await api.get(`/inbox/threads/${t.id}`);
    setActive(r.data);
  };
  const setTriage = async (tid, v) => {
    await api.put(`/inbox/threads/${tid}/triage`, { triage: v });
    setActive(a => a ? { ...a, thread: { ...a.thread, triage: v } } : a);
    setThreads(rows => rows.map(r => r.id === tid ? { ...r, triage: v } : r));
  };
  const draftTask = async (tid) => {
    const r = await api.post(`/inbox/threads/${tid}/task_draft`, {});
    toast.success("Task draft ready — open Quick Add to review", { duration: 3500 });
    // Stash the draft in sessionStorage for Quick Add to pick up on next open.
    sessionStorage.setItem("inbox_task_draft", JSON.stringify(r.data));
  };

  if (!conn || conn.status === "not_connected" || conn.status === "disconnected" || conn.status === "reauthorization_required") {
    const reauth = conn?.status === "reauthorization_required";
    return (
      <div className="h-full overflow-y-auto p-8" data-testid="inbox-page">
        <div className="text-eyebrow mb-2">Inbox</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight mb-6">
          {reauth ? "Reconnect your inbox" : "Connect your inbox"}
        </h1>
        {reauth && (
          <div className="mb-4 max-w-xl p-4 rounded-lg border border-amber-400/30 bg-amber-500/10 text-amber-100 text-sm" data-testid="inbox-reauth-banner">
            Your Gmail authorization is no longer valid. Reconnect to resume syncing — no messages have been lost; historical threads remain in HQ.
            {conn?.last_error && <div className="mt-1 text-[11px] text-amber-200/70">{conn.last_error}</div>}
          </div>
        )}
        <div className="p-6 rounded-xl border border-white/10 bg-[#151A23] max-w-xl space-y-4">
          <p className="text-sm text-white/70">Read-only Gmail connection. Alias HQ never sends email on your behalf.</p>
          <Button onClick={connect} className="rounded-full bg-white text-black hover:bg-white/90" data-testid="inbox-connect-btn">
            <Mail className="w-4 h-4 mr-2" /> {reauth ? "Reconnect Gmail" : "Connect Gmail"}
          </Button>
          <p className="text-[11px] text-white/40">Scope requested: <code>gmail.readonly</code>. No send permission is requested.</p>
        </div>
      </div>
    );
  }

  // Gmail per-user or daily quota exhaustion. This is NOT a reauth
  // failure — cursor and retry queue are intact, HQ will resume on the
  // next scheduled Sync once Google's window resets. Kept explicit so
  // the executive never thinks anything is broken.
  const rateLimited = conn?.status === "rate_limited";
  const rateLimitedMessage = conn?.last_error
    || (conn?.quota_kind === "daily_limited"
        ? "Gmail daily quota reached. HQ will resume automatically after the quota window resets."
        : "Gmail rate limit reached. HQ will retry automatically.");

  return (
    <div className="h-full overflow-y-auto p-4 sm:p-8" data-testid="inbox-page">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-eyebrow mb-1">Inbox</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">{conn.account_email}</h1>
          <p className="text-xs text-white/50 mt-1">Read-only. No agent send capability.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={recoverySync} disabled={busy} variant="ghost"
            className="rounded-full text-white/70 hover:text-white hover:bg-white/10 text-xs"
            data-testid="inbox-recovery-sync-btn"
            title="Rewind cursor and re-list the last 30 days. Recovers messages that failed before the retry queue existed.">
            Recovery sync
          </Button>
          <Button onClick={disconnect} variant="ghost" className="text-white/60 hover:text-white hover:bg-white/10">Disconnect</Button>
        </div>
      </div>

      <GmailStatusCard onSync={sync} onReconnect={connect} busy={busy} />

      {/* Temporary diagnostic — pulls /api/inbox/queue_status through the
          authenticated HQ API client. Auth is NEVER weakened; this UI
          only surfaces what the endpoint returns to an already-signed-in
          admin. Panel is dismissible. */}
      <div className="mb-4 flex items-center gap-2">
        <Button onClick={async () => {
          try {
            const r = await api.get("/inbox/queue_status");
            setDiag(r.data);
          } catch (e) {
            toast.error("Queue diagnostic failed");
          }
        }} variant="ghost" className="rounded-full text-white/70 hover:text-white hover:bg-white/10 text-xs" data-testid="inbox-queue-diagnostic-btn">
          Queue diagnostic
        </Button>
        {diag && (
          <button onClick={() => setDiag(null)} className="text-white/40 text-xs hover:text-white/70" data-testid="inbox-diag-close">Close</button>
        )}
      </div>

      {diag && (() => {
        // Derive the specific fields the executive wants — no other data shown.
        const lastScan = [...(diag.last_sync_log || [])].find(r => r.kind === "retry_queue_scan");
        const lastDrained = [...(diag.last_sync_log || [])].find(r => r.kind === "retry_queue_drained");
        return (
          <div className="mb-4 p-4 rounded-xl border border-amber-400/30 bg-amber-500/5 text-amber-100 text-xs" data-testid="inbox-diag-panel">
            <div className="font-mono grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
              <div><span className="text-amber-200/60">queue_size:</span> {diag.queue_size}</div>
              <div><span className="text-amber-200/60">history_id:</span> {diag.connection?.history_id ?? "null"}</div>
              <div className="sm:col-span-2">
                <span className="text-amber-200/60">queue_by_error_type:</span>{" "}
                {Object.keys(diag.queue_by_error_type || {}).length
                  ? Object.entries(diag.queue_by_error_type).map(([k,v]) => `${k}=${v}`).join(", ")
                  : "(empty)"}
              </div>
              <div className="sm:col-span-2">
                <span className="text-amber-200/60">queue_by_category:</span>{" "}
                {Object.keys(diag.queue_by_category || {}).length
                  ? Object.entries(diag.queue_by_category).map(([k,v]) => `${k}=${v}`).join(", ")
                  : "(empty)"}
              </div>
              <div className="sm:col-span-2">
                <span className="text-amber-200/60">queue_by_http_status:</span>{" "}
                {Object.keys(diag.queue_by_http_status || {}).length
                  ? Object.entries(diag.queue_by_http_status).map(([k,v]) => `${k}=${v}`).join(", ")
                  : "(empty)"}
              </div>
              <div className="sm:col-span-2">
                <span className="text-amber-200/60">queue_by_google_reason:</span>{" "}
                {Object.keys(diag.queue_by_google_reason || {}).length
                  ? Object.entries(diag.queue_by_google_reason).map(([k,v]) => `${k}=${v}`).join(", ")
                  : "(empty)"}
              </div>
              <div><span className="text-amber-200/60">last_sync_at:</span> {diag.connection?.last_sync_at ?? "null"}</div>
              <div><span className="text-amber-200/60">status:</span> {diag.connection?.status ?? "?"}</div>
              <div className="sm:col-span-2 text-amber-200/60 text-[10px]">last_error: <span className="text-amber-100 break-all">{diag.connection?.last_error || "—"}</span></div>
              <div className="sm:col-span-2 mt-2 pt-2 border-t border-amber-400/20">
                <span className="text-amber-200/60">most recent retry_queue_scan:</span>{" "}
                {lastScan
                  ? `queue_size_seen=${lastScan.meta?.queue_size_seen} at ${lastScan.at?.slice(0,19)}`
                  : "(never)"}
              </div>
              <div className="sm:col-span-2">
                <span className="text-amber-200/60">most recent retry_queue_drained:</span>{" "}
                {lastDrained
                  ? `attempted=${lastDrained.meta?.attempted}, recovered=${lastDrained.meta?.recovered}, still_failing=${lastDrained.meta?.still_failing} at ${lastDrained.at?.slice(0,19)}`
                  : "(never)"}
              </div>
            </div>
          </div>
        );
      })()}

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <button onClick={() => setFilter("")}
          className={`px-3 py-1 rounded-full text-xs ${!filter ? "bg-white text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
          data-testid="inbox-filter-all">All</button>
        {TRIAGE.map(t => (
          <button key={t.v} onClick={() => setFilter(t.v)}
            className={`px-3 py-1 rounded-full text-xs ${filter === t.v ? "bg-white text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
            data-testid={`inbox-filter-${t.v}`}>{t.label}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4">
        <div className="space-y-1.5 max-h-[70vh] overflow-y-auto" data-testid="inbox-thread-list">
          {threads.length === 0 && (
            <div className="text-white/50 text-sm p-6 rounded-lg border border-white/10 bg-[#151A23]">No threads yet.</div>
          )}
          {threads.map(t => (
            <button key={t.id} onClick={() => openThread(t)}
              className={`w-full text-left p-3 rounded-lg border ${active?.thread?.id === t.id ? "border-white/30 bg-white/[0.04]" : "border-white/10 bg-[#151A23] hover:border-white/20"}`}
              data-testid={`inbox-thread-${t.id}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-wider text-white/40">{t.triage.replace("_"," ")}</span>
                <span className="text-[10px] text-white/40">{(t.latest_message_at || "").slice(0,10)}</span>
              </div>
              <div className="text-sm font-medium truncate">{t.subject || "(no subject)"}</div>
              <div className="text-xs text-white/50 truncate">{(t.participants||[]).map(p=>p.email).join(", ")}</div>
              {t.message_count > 1 && <div className="text-[11px] text-white/40 mt-0.5">{t.message_count} messages</div>}
            </button>
          ))}
        </div>

        <div>
          {active ? (
            <div className="p-4 sm:p-5 rounded-xl border border-white/10 bg-[#151A23]" data-testid="inbox-thread-detail">
              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div>
                  <div className="font-display text-lg font-semibold">{active.thread.subject || "(no subject)"}</div>
                  <div className="text-xs text-white/50 mt-1">
                    {active.thread.message_count} messages · {(active.thread.participants||[]).length} participants
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={active.thread.triage} onValueChange={(v) => setTriage(active.thread.id, v)}>
                    <SelectTrigger className="w-40 bg-white/5 border-white/10 h-8 text-xs" data-testid="inbox-triage-select"><SelectValue/></SelectTrigger>
                    <SelectContent className="bg-[#151A23] border-white/10 text-white">
                      {TRIAGE.map(t => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={() => draftTask(active.thread.id)} className="rounded-full bg-white text-black hover:bg-white/90" data-testid="inbox-create-task-btn">Create Task</Button>
                </div>
              </div>

              <div className="space-y-4">
                {(active.messages || []).map(m => (
                  <div key={m.id} className="border-t border-white/5 pt-4">
                    <div className="text-xs text-white/50 mb-1">
                      <span className="text-white/80 font-medium">{m.sender.name || m.sender.email}</span>{" "}
                      <span>&lt;{m.sender.email}&gt;</span>{" · "}
                      <span>{(m.received_at || "").slice(0,16).replace("T"," ")}</span>
                    </div>
                    <div className="text-sm text-white/85 whitespace-pre-wrap break-words">{m.text_body}</div>
                    {m.attachments?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {m.attachments.map((a, i) => (
                          <span key={i} className="text-[11px] px-2 py-1 rounded bg-white/5 border border-white/10 inline-flex items-center gap-1">
                            <Paperclip className="w-3 h-3" /> {a.filename}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-white/40 text-sm p-8 rounded-xl border border-white/10 bg-[#151A23]">Select a thread to read.</div>
          )}
        </div>
      </div>
    </div>
  );
}
